import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { HighlightPill } from './labels';

/**
 * Tiêu đề một nhóm câu — chế độ Xem trong app và trang công khai `/d/$token`.
 *
 * Phải khác hẳn tiêu đề câu ngay dưới nó: người dùng báo 2026-10-07 "kích thước
 * chữ của nhóm và đầu mỗi câu không khác biệt nên rất khó theo dõi". Ba cấp:
 * nhóm `text-xl` đậm + gạch chân dày (như trang mẫu gốc), câu `text-base`
 * semibold, phương án chữ thường `font-medium`.
 */
export function DecisionGroupHeading({
  code,
  title,
  highlight,
  note,
  answered,
  total,
  as: Heading = 'h3',
}: {
  readonly code: string | null;
  readonly title: string;
  readonly highlight: string | null;
  readonly note: string | null;
  readonly answered: number;
  readonly total: number;
  readonly as?: 'h2' | 'h3';
}): ReactNode {
  const { t } = useTranslation();
  return (
    <header className="flex flex-col gap-1.5 border-b-2 border-foreground pb-2">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        {code ? (
          <span className="rounded-md bg-foreground px-2 py-0.5 font-mono text-sm font-bold leading-5 text-background">
            {code}
          </span>
        ) : null}
        <Heading className="min-w-0 text-xl font-bold leading-7 tracking-tight [overflow-wrap:anywhere]">
          {title}
        </Heading>
        {highlight ? <HighlightPill>{highlight}</HighlightPill> : null}
        <span
          className={cn(
            'ml-auto text-sm tabular-nums',
            answered === total ? 'text-rag-green' : 'text-muted-foreground',
          )}
          data-testid="decision-group-count"
        >
          {t('decisionSheet.groupCount', { answered, total })}
        </span>
      </div>
      {note ? (
        <p className="whitespace-pre-line text-sm text-muted-foreground [overflow-wrap:anywhere]">
          {note}
        </p>
      ) : null}
    </header>
  );
}
